// @ts-strict-ignore
import isBoolean from 'lodash/isBoolean';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import merge from 'lodash/merge';
import pick from 'lodash/pick';
import moment, {Moment, MomentInput} from 'moment';
import momentTimezone from 'moment-timezone';
import {DateRange as CalendarJsDateRange} from 'react-day-picker';

import {Translatable} from 'common/i18n';
import type {Section} from 'common/components/items-menu/types';
import {CALENDAR_DATE_FORMAT, TIMESTAMP_FORMAT, Timezone} from 'common/constants';
import {FEATURE_GATES_UNLIMITED} from 'common/report/constants';
import {
  assertIsStandardTimeUnit,
  clampDateRange,
  convertRelativeDateToCalendarDateStr,
  convertRelativeDateToCalendarDateStrAsOf,
  DEFAULT_TIME_PERIOD_SETTINGS,
  getCurrentMoment,
  getCurrentMomentAsOf,
  getLocalTimezone,
  isAtEndOfDay,
  isAtStartOfDay,
  parseDate,
} from 'common/util/date';
import {TimePeriodSettings} from 'common/report/stores/types';

import {
  DATE_RANGE_TYPE_DISPLAY_NAMES,
  END_OF_YESTERDAY,
  NOW,
  PRESET_DATE_RANGE_DISPLAY_NAMES,
  PRESET_DATE_RANGE_SHORT_DISPLAY_NAMES,
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS,
  PresetDateRange,
  SPECIAL_DATE_RESOLVERS,
  SpecialDateString,
  START_OF_TODAY,
  START_OF_YESTERDAY,
} from './presets';
import {
  ApiDateRange,
  BaseDate,
  BookmarkDateRange,
  CalendarDateRange,
  DateRangeAttributes,
  DateRangeType,
  ExclusionOffset,
  RelativeDate,
  TimeUnit,
} from './types';
import {getDateType, isRelativeDate} from './utils';

export interface PresetOption {
  label: Translatable;
  shortLabel: Translatable;
  value: DateRangeAttributes;
}

const SUB_DAY_UNITS: TimeUnit[] = [TimeUnit.Hour, TimeUnit.Minute];

export class DateRange implements DateRangeAttributes {
  public static get presets(): PresetDateRange[] {
    return [];
  }

  public static getPresetOption(preset: PresetDateRange): PresetOption {
    return {
      label: PRESET_DATE_RANGE_DISPLAY_NAMES[preset],
      shortLabel: PRESET_DATE_RANGE_SHORT_DISPLAY_NAMES[preset],
      value: PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[preset],
    };
  }

  public static getPresetOptions(): PresetOption[] {
    return this.presets.map(DateRange.getPresetOption);
  }

  public static convertAbsoluteDateToCalendarDate(date: string, dateFormat?: string): string {
    if (date && date in SPECIAL_DATE_RESOLVERS) {
      const specialMoment = moment(
        SPECIAL_DATE_RESOLVERS[date as SpecialDateString](null, getLocalTimezone(), DEFAULT_TIME_PERIOD_SETTINGS),
      );
      return specialMoment.format(dateFormat || CALENDAR_DATE_FORMAT);
    }
    return moment(date).format(dateFormat || CALENDAR_DATE_FORMAT);
  }

  /**
   * Returns a moment object for the given date string.
   *
   * @param {string} date - Date string to convert to a moment object.
   * @param {string} projectTimezone - Timezone of the project. May be null if project context hasn't yet been initialized.
   * @param {Moment} asOfDate - Moment for the current time factoring in projectAsOfDate override, if one exists.
   * @param {TimePeriodSettings} timePeriodSettings - Optional settings for the time period.
   *
   * @returns {Moment} date as a moment object.
   */
  public static convertAbsoluteDateToMoment(
    date: string | undefined,
    projectTimezone: Timezone,
    asOfDate: Date,
    timePeriodSettings: TimePeriodSettings,
  ): Moment {
    if (date && date in SPECIAL_DATE_RESOLVERS) {
      return momentTimezone.tz(
        SPECIAL_DATE_RESOLVERS[date as SpecialDateString](asOfDate, projectTimezone, timePeriodSettings),
        projectTimezone,
      );
    } else if (projectTimezone) {
      return momentTimezone.tz(date ?? asOfDate, projectTimezone as string);
    }
    return moment(date ?? asOfDate);
  }

  /**
   * @deprecated
   *
   * NOTE: You probably want to use convertCalendarDateToRelativeDateAsOf and pass the projectAsOfDate since that's needed to correctly
   * compute the date range when working with relative dates.
   * This method localizes to local browser time.
   */
  public static convertCalendarDateToRelativeDate(dateStr: string): RelativeDate {
    return this.convertCalendarDateToRelativeDateAsOf(
      dateStr,
      getCurrentMoment({localized: true}).format(TIMESTAMP_FORMAT),
      getLocalTimezone(),
    );
  }

  public static convertCalendarDateToRelativeDateAsOf(
    date: string | Date,
    asOfDate: string | null,
    timezone: Timezone | null,
  ): RelativeDate | null {
    if (!date) {
      return null;
    }
    const asOfMoment = getCurrentMomentAsOf(asOfDate, timezone).startOf(`day`);
    const calendarMoment = momentTimezone.tz(date, timezone).startOf(`day`);
    const daysDiff = asOfMoment.diff(calendarMoment, `days`);
    return {
      unit: TimeUnit.Day,
      value: daysDiff,
    };
  }

  public static fromBookmarkFormat(bookmarkDateRange: BookmarkDateRange): DateRange {
    const {to_date: toDate, from_date: fromDate, type, exclusion_offset: exclusionOffset} = bookmarkDateRange;
    const attrs: DateRangeAttributes = {type, exclusionOffset};
    switch (type) {
      case DateRangeType.RelativeAfter: {
        attrs.window = fromDate as RelativeDate;
        break;
      }
      case DateRangeType.RelativeBefore: {
        attrs.window = toDate as RelativeDate;
        break;
      }
      case DateRangeType.Between:
      case DateRangeType.On: {
        if (isRelativeDate(fromDate)) {
          attrs.rolling = true;
        }
        attrs.from = fromDate;
        attrs.to = toDate;
        break;
      }
      case DateRangeType.Since: {
        attrs.from = fromDate;
        break;
      }
      case DateRangeType.StartsOn:
      case DateRangeType.EndsOn:
        attrs.from = fromDate;
        attrs.to = toDate;
        break;
      default: {
        return null;
      }
    }

    return new DateRange(attrs);
  }

  /**
   * Checks the input dateRangeAttrs to see if it has hour/minute granularity for Between, Since, or
   * Last date ranges.
   *
   * @param {DateRangeAttributes} dateRangeAttrs
   * @returns true if the dateRangeAttrs has hour/minute granularity specified, false otherwise.
   */
  public static hasSubDayGranularity(
    dateRangeAttrs: Partial<DateRangeAttributes> | undefined,
    projectTz: Timezone,
  ): boolean {
    if (!dateRangeAttrs) {
      return false;
    }
    if (dateRangeAttrs.window) {
      return SUB_DAY_UNITS.includes(dateRangeAttrs.window.unit);
    }

    const fromDateType = getDateType(dateRangeAttrs.from);
    const toDateType = getDateType(dateRangeAttrs.to);
    if (
      fromDateType === `relative` &&
      (dateRangeAttrs.from as RelativeDate) &&
      SUB_DAY_UNITS.includes((dateRangeAttrs.from as RelativeDate).unit)
    ) {
      return true;
    }
    if (
      toDateType === `relative` &&
      (dateRangeAttrs.to as RelativeDate) &&
      SUB_DAY_UNITS.includes((dateRangeAttrs.to as RelativeDate).unit)
    ) {
      return true;
    }

    const dateStringHasSubDayGranularity = (dateStr: string) => {
      if (dateStr.split(`T`).length !== 2) {
        return false;
      }
      const jsDate = momentTimezone.tz(dateStr, TIMESTAMP_FORMAT, projectTz).toDate();
      return !isAtStartOfDay(jsDate, projectTz) && !isAtEndOfDay(jsDate, projectTz);
    };
    if (
      fromDateType === `absolute` &&
      typeof dateRangeAttrs.from === `string` &&
      dateStringHasSubDayGranularity(dateRangeAttrs.from)
    ) {
      return true;
    }
    return (
      toDateType === `absolute` &&
      typeof dateRangeAttrs.to === `string` &&
      dateStringHasSubDayGranularity(dateRangeAttrs.to)
    );
  }

  declare public type: DateRangeType;
  declare public from?: BaseDate;
  declare public to?: BaseDate;
  declare public window?: RelativeDate;
  declare public exclusionOffset?: ExclusionOffset | null;
  declare public rolling?: boolean;

  constructor(attributes: Partial<DateRangeAttributes> = {}) {
    attributes = attributes ?? {};
    this.type = attributes.type ?? this.getDefaultType();
    this.exclusionOffset = attributes.exclusionOffset ?? null;

    switch (this.type) {
      case DateRangeType.RelativeFuture:
      case DateRangeType.RelativeAfter:
      case DateRangeType.NotRelativeAfter:
      case DateRangeType.RelativeBefore: {
        this.window = attributes.window ?? this.getDefaultWindow();
        break;
      }
      case DateRangeType.EndsOn:
      case DateRangeType.StartsOn:
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Before:
      case DateRangeType.Between:
      case DateRangeType.NotBetween:
      case DateRangeType.Since: {
        attributes.to && (this.to = attributes.to);
        attributes.from && (this.from = attributes.from);
        if (isBoolean(attributes.rolling)) {
          this.rolling = attributes.rolling;
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  /**
   * @deprecated missing asOfDate, timezone and timePeriodSettings. This could result in incorrect dates.
   * Use getCalendarDatesAsOf with all params specified instead. Param values should be extracted from the
   * project time configs in most cases.
   */
  public getCalendarDates(dateFormat = CALENDAR_DATE_FORMAT): CalendarDateRange {
    return this.getCalendarDatesAsOf(null, dateFormat);
  }

  /**
   * @deprecated missing timezone and timePeriodSettings. could result in incorrect dates.
   * Use getCalendarDatesAsOf with all params specified instead. Param values should be extracted from the
   * project time configs in most cases.
   */
  public getCalendarDatesAsOf(asOfDateStr?: string | null, dateFormat?: string): CalendarDateRange;

  /**
   * @deprecated missing timePeriodSettings. could result in incorrect dates.
   * Use getCalendarDatesAsOf with all params specified instead. Param values should be extracted from the
   * project time configs in most cases.
   */
  public getCalendarDatesAsOf(asOfDateStr?: string | null, dateFormat?: string, timezone?: Timezone): CalendarDateRange;

  public getCalendarDatesAsOf(
    asOfDateStr?: string | null,
    dateFormat?: string,
    timezone?: Timezone,
    timePeriodSettings?: TimePeriodSettings,
  ): CalendarDateRange;

  public getCalendarDatesAsOf(
    asOfDateStr?: string | null,
    dateFormat?: string,
    timezone?: Timezone,
    timePeriodSettings?: TimePeriodSettings,
  ): CalendarDateRange {
    if (!timePeriodSettings) {
      // Default to Monday week start and January quarter start.
      timePeriodSettings = DEFAULT_TIME_PERIOD_SETTINGS;
    }
    if (!timezone) {
      // Default to localTz
      timezone = getLocalTimezone();
    }
    // Defaults to current time
    const asOfDate = getCurrentMomentAsOf(asOfDateStr, timezone);
    const dateGranularity = this.hasSubDayGranularity(timezone) ? `minute` : `day`;
    let fromMoment;
    let toMoment;
    if (this.window && this.isRelativeWindow()) {
      const absoluteDateString = convertRelativeDateToCalendarDateStrAsOf({
        relativeDate: this.window as RelativeDate,
        asOfDate: asOfDate.toDate(),
        projectTz: timezone as Timezone,
        timePeriodSettings,
        dateFormat,
      });
      fromMoment = moment(absoluteDateString);
      toMoment = asOfDate;
      // handle future relative windows
      if (this.type === DateRangeType.RelativeFuture) {
        fromMoment = moment(asOfDate).startOf(dateGranularity);
        toMoment = moment.utc(
          convertRelativeDateToCalendarDateStrAsOf({
            relativeDate: {unit: this.window.unit, value: this.window.value * -1} as RelativeDate,
            asOfDate: asOfDate.toDate(),
            projectTz: timezone as Timezone,
            timePeriodSettings,
            dateFormat,
          }),
        );
      }
      return {
        from: fromMoment.format(dateFormat || CALENDAR_DATE_FORMAT),
        to: toMoment.format(dateFormat || CALENDAR_DATE_FORMAT),
      };
    }

    if (isRelativeDate(this.from)) {
      fromMoment = moment.utc(
        convertRelativeDateToCalendarDateStrAsOf({
          relativeDate: this.from as RelativeDate,
          asOfDate: asOfDate.toDate(),
          projectTz: timezone as Timezone,
          timePeriodSettings,
          dateFormat,
        }),
      );
    } else {
      fromMoment = DateRange.convertAbsoluteDateToMoment(
        this.from as string,
        timezone,
        asOfDate.toDate(),
        timePeriodSettings,
      );
    }

    if (isRelativeDate(this.to)) {
      toMoment = moment.utc(
        convertRelativeDateToCalendarDateStrAsOf({
          relativeDate: this.to as RelativeDate,
          asOfDate: asOfDate.toDate(),
          projectTz: timezone as Timezone,
          timePeriodSettings,
          dateFormat,
        }),
      );
    } else {
      toMoment = DateRange.convertAbsoluteDateToMoment(
        this.to as string,
        timezone,
        asOfDate.toDate(),
        timePeriodSettings,
      );
    }

    return {
      from: fromMoment.format(dateFormat || CALENDAR_DATE_FORMAT),
      to: toMoment.format(dateFormat || CALENDAR_DATE_FORMAT),
    };
  }

  public getCalendarJsDatesAsOf(
    projectAsOfDate: string | null,
    projectTz: Timezone,
    timePeriodSettings: TimePeriodSettings,
  ): CalendarJsDateRange | undefined {
    const calendarDateStrRange = this.getCalendarDatesAsOf(
      projectAsOfDate,
      TIMESTAMP_FORMAT,
      projectTz,
      timePeriodSettings,
    );
    if (!calendarDateStrRange.from || !calendarDateStrRange.to) {
      return undefined;
    }
    return {
      from: momentTimezone.tz(calendarDateStrRange.from, TIMESTAMP_FORMAT, projectTz).toDate(),
      to: momentTimezone.tz(calendarDateStrRange.to, TIMESTAMP_FORMAT, projectTz).toDate(),
    };
  }

  public getIntervalDays(
    asOfDateStr: string | null = null,
    projectTz?: Timezone,
    timePeriodSettings?: TimePeriodSettings,
  ): number {
    const {to, from} = this.getCalendarDatesAsOf(asOfDateStr, CALENDAR_DATE_FORMAT, projectTz, timePeriodSettings);

    return moment(to).diff(moment(from), TimeUnit.Day);
  }

  public canUseRolling(): boolean {
    return false;
  }

  public getBookmarkFormat(): BookmarkDateRange {
    const bookmarkDateRange: Partial<BookmarkDateRange> = {type: this.type, exclusion_offset: this.exclusionOffset};
    switch (this.type) {
      case DateRangeType.RelativeAfter:
      case DateRangeType.RelativeFuture: {
        bookmarkDateRange.from_date = this.window;
        bookmarkDateRange.to_date = NOW;
        break;
      }
      case DateRangeType.RelativeBefore: {
        bookmarkDateRange.to_date = this.window;
        break;
      }
      case DateRangeType.StartsOn:
      case DateRangeType.EndsOn:
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        bookmarkDateRange.from_date = this.from;
        bookmarkDateRange.to_date = this.to;
        break;
      }
      case DateRangeType.Since: {
        bookmarkDateRange.from_date = this.from;
        bookmarkDateRange.to_date = NOW;
        break;
      }
      default: {
        return null;
      }
    }

    return bookmarkDateRange as BookmarkDateRange;
  }

  public getDefaultType(): DateRangeType {
    return DateRangeType.RelativeAfter;
  }

  public getDefaultWindow(): RelativeDate {
    return {
      unit: TimeUnit.Day,
      value: 7,
    };
  }

  public getDateRangeTypeOptions(): DateRangeType[] {
    return [DateRangeType.RelativeAfter, DateRangeType.Between, DateRangeType.Since, DateRangeType.On];
  }

  public getTimeUnitOptions(): TimeUnit[] {
    return [TimeUnit.Hour, TimeUnit.Day, TimeUnit.Week, TimeUnit.Month];
  }

  public getDateRangeTypeDisplaySections(selectedType): Section[] {
    const items = this.getDateRangeTypeOptions().map((type) => ({
      value: DATE_RANGE_TYPE_DISPLAY_NAMES[type],
      label: DATE_RANGE_TYPE_DISPLAY_NAMES[type],
      isSelected: type === selectedType,
    }));
    return [{items}];
  }

  public getAttributes(): DateRangeAttributes {
    return pick(this, [`type`, `from`, `to`, `window`, `rolling`, `exclusionOffset`]);
  }

  public mergeAttributes(attributes: Partial<DateRangeAttributes>): this {
    // Use of merge has potential security issues, since we are using lodash >= 4.17.5 it is safe
    // eslint-disable-next-line prototype-pollution-security-rules/detect-merge-objects
    return new (this.constructor as any)(merge(this.getAttributes(), attributes));
  }

  public equals(otherRange: DateRange): boolean {
    return (
      this.type === otherRange.type &&
      isEqual(this.from, otherRange.from) &&
      isEqual(this.to, otherRange.to) &&
      isEqual(this.window, otherRange.window) &&
      isEqual(this.exclusionOffset, otherRange.exclusionOffset) &&
      this.rolling === otherRange.rolling
    );
  }

  public isValid(maxHistoryDays?: number): boolean {
    if (maxHistoryDays) {
      return this.getIntervalDays() <= maxHistoryDays;
    }

    return this._isValid();
  }

  public isWithinMaxHistoryLimit(maxHistoryDays: number): boolean {
    if (!this.isValid()) {
      return false;
    }
    if (maxHistoryDays === FEATURE_GATES_UNLIMITED) {
      return true;
    }

    let fromMoment = null;
    if (this.isRelativeWindow()) {
      const {value, unit} = this.window;
      assertIsStandardTimeUnit(unit);
      fromMoment = getCurrentMoment({localized: true}).subtract(value, unit);
    } else if (typeof this.from === `string` && this.from in SPECIAL_DATE_RESOLVERS) {
      fromMoment = moment(
        SPECIAL_DATE_RESOLVERS[this.from as SpecialDateString](null, getLocalTimezone(), DEFAULT_TIME_PERIOD_SETTINGS),
      );
    } else {
      fromMoment = moment(this.from as string);
    }
    const minDayMoment = getCurrentMoment({localized: true}).subtract(maxHistoryDays, `days`);
    return fromMoment.isSameOrAfter(minDayMoment);
  }

  public isValidRelativeDate(date: RelativeDate): boolean {
    if (!date) {
      return false;
    }
    const {value, unit} = date;
    return this.getTimeUnitOptions().includes(unit) && isNumber(value) && value >= 0;
  }

  public isValidRollingRelativeDate(date: RelativeDate): boolean {
    if (!date) {
      return false;
    }
    const {value, unit} = date;
    return this.getTimeUnitOptions().includes(unit) && isNumber(value) && Math.abs(value) >= 0;
  }

  public isValidAbsoluteDate(date: string): boolean {
    return date in SPECIAL_DATE_RESOLVERS || !isNil(parseDate(date));
  }

  /**
   * Checks if this date range has time specified.
   *
   * @returns {boolean} true if this date range has hours, minutes, or seconds specified.
   */
  public hasSubDayGranularity(projectTz: Timezone): boolean {
    return DateRange.hasSubDayGranularity(this, projectTz);
  }

  public getCohortApiFormat(): ApiDateRange {
    // Cohorts has a slightly different format for relative before. this should work for other APIs as well.
    switch (this.type) {
      case DateRangeType.RelativeFuture:
      case DateRangeType.RelativeAfter:
      case DateRangeType.NotRelativeAfter: {
        return {window: this.window};
      }
      case DateRangeType.RelativeBefore: {
        return {to_date: this.window};
      }
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        return {
          from_date: this.from,
          to_date: this.to,
        };
      }
      case DateRangeType.Since: {
        return {from_date: this.from};
      }
      case DateRangeType.Before: {
        return {to_date: this.to};
      }
      default: {
        return null;
      }
    }
  }

  public getCalendarMaxDate(): string | null {
    return null;
  }

  public isRelativeWindow(): boolean {
    return [
      DateRangeType.RelativeAfter,
      DateRangeType.NotRelativeAfter,
      DateRangeType.RelativeBefore,
      DateRangeType.RelativeFuture,
    ].includes(this.type);
  }

  public getCalendarDate(date: BaseDate): string {
    if (this.isRelativeWindow() || !date) {
      return null;
    }

    if (this.rolling) {
      return convertRelativeDateToCalendarDateStr(date as RelativeDate);
    } else if (typeof date === `string` && date in SPECIAL_DATE_RESOLVERS) {
      const value = [START_OF_YESTERDAY, END_OF_YESTERDAY].includes(date) ? 1 : 0;
      return convertRelativeDateToCalendarDateStr({unit: TimeUnit.Day, value});
    }
    return date as string;
  }

  public getCalendarFromDate(): string {
    return this.getCalendarDate(this.from);
  }

  public getCalendarToDate(): string {
    return this.getCalendarDate(this.to);
  }

  /** Returns a copy of this date range, clamped to fit within limits.
   *
   * @param limit - A pair of dates, setting a minimum and maximum date for the range.
   */
  public clamped(limit: [MomentInput, MomentInput], timezone: Timezone) {
    if (!this.from || !this.to) {
      throw new Error(`Only date ranges can be clamped`);
    }
    const formatToUse = this.hasSubDayGranularity(timezone) ? TIMESTAMP_FORMAT : CALENDAR_DATE_FORMAT;
    const updated = clampDateRange({
      from: this.getCalendarFromDate(),
      to: this.getCalendarToDate(),
      limit,
      format: formatToUse,
    });

    return new DateRange({
      ...this.getAttributes(),
      from: updated.from.format(formatToUse),
      to: updated.to.format(formatToUse),
    });
  }

  public isRolling(): boolean {
    return !!this.rolling || [START_OF_TODAY, START_OF_YESTERDAY].includes(this.from as SpecialDateString);
  }

  public checkFromDateBounds(
    projectTz: Timezone,
    projectAsOfDate: string | null,
    timePeriodSettings: TimePeriodSettings,
  ): `BeyondFrozenDate` | `Future` | `Past` {
    const projectAsOfMoment = getCurrentMomentAsOf(projectAsOfDate, projectTz);
    const endOfAsOfDate = projectAsOfMoment.endOf(`day`).toDate();
    const calendarDateRange = this.getCalendarJsDatesAsOf(projectAsOfDate, projectTz, timePeriodSettings);
    const beyondAsOfDate = calendarDateRange?.from && calendarDateRange.from > endOfAsOfDate;
    if (beyondAsOfDate) {
      // If the project has a frozen date, return `BeyondFrozenDate`.
      // Otherwise the date is fully in the future
      return projectAsOfDate ? `BeyondFrozenDate` : `Future`;
    }
    return `Past`;
  }

  public hasFutureFromDate(
    projectTz: Timezone,
    projectAsOfDate: string | null,
    timePeriodSettings: TimePeriodSettings,
  ): boolean {
    return this.checkFromDateBounds(projectTz, projectAsOfDate, timePeriodSettings) !== `Past`;
  }

  public hasFutureToDate(
    projectTz: Timezone,
    projectAsOfDate: string | null,
    timePeriodSettings: TimePeriodSettings,
  ): boolean {
    const projectAsOfMoment = getCurrentMomentAsOf(projectAsOfDate, projectTz);
    const endOfAsOfDate = projectAsOfMoment.endOf(`day`).toDate();
    const calendarDateRange = this.getCalendarJsDatesAsOf(projectAsOfDate, projectTz, timePeriodSettings);
    return calendarDateRange?.to && calendarDateRange.to > endOfAsOfDate;
  }

  private _isValid(): boolean {
    if (!this.getDateRangeTypeOptions().includes(this.type)) {
      return false;
    }

    switch (this.type) {
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Since: {
        if (this.rolling) {
          return this.isValidRollingRelativeDate(this.from as RelativeDate);
        }

        return this.isValidAbsoluteDate(this.from as string);
      }
      case DateRangeType.Before:
        return this.isValidAbsoluteDate(this.to as string);
      case DateRangeType.NotBetween:
      case DateRangeType.Between: {
        const fromType = getDateType(this.from);
        const toType = getDateType(this.to);
        if (this.rolling || (fromType === `relative` && toType === `relative`)) {
          return (
            Boolean(this.rolling) &&
            this.isValidRollingRelativeDate(this.from as RelativeDate) &&
            this.isValidRollingRelativeDate(this.to as RelativeDate) &&
            (this.from as RelativeDate).unit === (this.to as RelativeDate).unit
          );
        }
        return (
          (this.isValidAbsoluteDate(this.from as string) ||
            this.isValidRollingRelativeDate(this.from as RelativeDate)) &&
          (this.isValidAbsoluteDate(this.to as string) || this.isValidRollingRelativeDate(this.to as RelativeDate))
        );
      }
      case DateRangeType.RelativeFuture:
      case DateRangeType.RelativeBefore:
      case DateRangeType.RelativeAfter:
      case DateRangeType.NotRelativeAfter: {
        return this.isValidRelativeDate(this.window);
      }
      default:
        return false;
    }
  }
}
